import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/shared/utils';
import {
  LayoutDashboard,
  FolderKanban,
  Server,
  Boxes,
  History,
  Package,
  Cable,
  RefreshCw,
  Search,
} from 'lucide-react';

interface SearchItem {
  id: string;
  label: string;
  group: string;
  to: string;
  icon?: React.ReactNode;
}

const PAGE_ITEMS: SearchItem[] = [
  { id: 'page-dashboard', label: 'Dashboard', group: 'Pages', to: '/dashboard', icon: <LayoutDashboard className="h-4 w-4" /> },
  { id: 'page-projects', label: 'Projects', group: 'Pages', to: '/projects', icon: <FolderKanban className="h-4 w-4" /> },
  { id: 'page-environments', label: 'Environments', group: 'Pages', to: '/environments', icon: <Server className="h-4 w-4" /> },
  { id: 'page-apps', label: 'Container Apps', group: 'Pages', to: '/apps', icon: <Boxes className="h-4 w-4" /> },
  { id: 'page-history', label: 'Recent Changes', group: 'Pages', to: '/history', icon: <History className="h-4 w-4" /> },
  { id: 'page-images', label: 'Image Repositories', group: 'Pages', to: '/images', icon: <Package className="h-4 w-4" /> },
  { id: 'page-connections', label: 'Connections', group: 'Pages', to: '/connections', icon: <Cable className="h-4 w-4" /> },
  { id: 'page-sync', label: 'Sync Status', group: 'Pages', to: '/sync', icon: <RefreshCw className="h-4 w-4" /> },
];

interface CommandPaletteProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly extraItems?: SearchItem[];
}

export type { SearchItem };

export default function CommandPalette({ open, onOpenChange, extraItems = [] }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const allItems = useMemo(() => [...PAGE_ITEMS, ...extraItems], [extraItems]);

  const filtered = useMemo(() => {
    if (!query.trim()) return allItems;
    const lower = query.toLowerCase();
    return allItems.filter(
      (item) =>
        item.label.toLowerCase().includes(lower) ||
        item.group.toLowerCase().includes(lower),
    );
  }, [allItems, query]);

  // Group results
  const grouped = useMemo(() => {
    const groups: Record<string, SearchItem[]> = {};
    for (const item of filtered) {
      if (!groups[item.group]) groups[item.group] = [];
      groups[item.group].push(item);
    }
    return groups;
  }, [filtered]);

  const flatFiltered = useMemo(() => filtered, [filtered]);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      // Focus input on next tick
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelect = useCallback(
    (item: SearchItem) => {
      navigate(item.to);
      onOpenChange(false);
    },
    [navigate, onOpenChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, flatFiltered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatFiltered[selectedIndex]) {
      e.preventDefault();
      handleSelect(flatFiltered[selectedIndex]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-lg overflow-hidden">
        <div className="flex items-center border-b px-3">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, projects, environments..."
            className="border-0 shadow-none focus-visible:ring-0 h-12"
          />
        </div>
        <div className="max-h-[300px] overflow-y-auto p-2">
          {flatFiltered.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">No results found.</p>
          )}
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group} className="mb-2">
              <p className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {group}
              </p>
              {items.map((item) => {
                const idx = flatFiltered.indexOf(item);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSelect(item)}
                    className={cn(
                      'flex items-center gap-3 w-full rounded-md px-2 py-2 text-sm text-left transition-colors',
                      idx === selectedIndex
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent/50',
                    )}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="border-t px-3 py-2 text-xs text-muted-foreground flex items-center gap-4">
          <span><kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs">↑↓</kbd> Navigate</span>
          <span><kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs">Enter</kbd> Select</span>
          <span><kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs">Esc</kbd> Close</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
